import CloudUser from './CloudUser'
import ClientAgent from '../clientAgent'
import {CloudUserList} from './CloudUserList'
import ProjectDetailsPermission from './Response/ProjectDetailsPermission'
import CloudProjectServiceGroup from './Response/CloudProjectServiceGroup'

export class CloudProject {
  _ca = null
  _idTerminationPoint = null
  _name = null
  _owner = null
  _maintainer = null
  /** @type {ProjectDetailsPermissions} */
  _permissions = null
  /** @type {Array<CloudProjectServiceGroup>} */
  _serviceGroups = []
  _projectId = null
  _statusName = null
  _statusColor = null
  _loginUrl = null
  /**
   * @type {CloudUserList}
   * @private
   */
  _users = new CloudUserList()
  _detailsLoaded = null
  _siteId = null
  _siteName = null

  /**
   * @param {string} permission
   * @param {string} email
   * @returns {Promise<any>}
   */
  inviteUserToProject (permission, email) {
    return this.ca.makeRequest('openStack/servisne/grantPermissionForProjectToEmail', {
      idTerminationPoint: this._idTerminationPoint,
      permission: permission,
      email: email
    })
  }

  /**
   * @param {number} idTerminationPoint
   * @param {string} name
   * @param {boolean} owner
   * @param {boolean} maintainer
   * @param {array<{id: number, ownUser: boolean, username: string}>} users
   * @param {number} siteId
   * @param {string} siteName
   */
  loadBasicProjectData (idTerminationPoint, name, owner, maintainer, users, siteId, siteName) {
    this._idTerminationPoint = idTerminationPoint
    this._name = name
    this._owner = owner
    this._maintainer = maintainer
    users.forEach((user) => {
      let cloudUser = CloudUser.getInstance(user.id)
      cloudUser.loadBasicUserData(user.id, user.ownUser, user.username, user.siteId, user.ownerEmail, user.email, user.gsm)
      this._users.addUserToList(cloudUser)
    })
    this._detailsLoaded = false
    this._siteId = siteId
    this._siteName = siteName
  }

  async loadProjectDetails () {
    if (this._idTerminationPoint === null) {
      throw new Error('Napaka - ne najdem projekta')
    }
    if (!this._detailsLoaded) {
      /** @type {ProjectDetailsResponse} */
      let details = await this.ca.makeRequest('openStack/servisne/getProjectDetails', {
        idTerminationPoint: this._idTerminationPoint
      })
      this._permissions = {
        granted: [],
        requests: []
      }
      this._serviceGroups = []
      if (details.permissions !== null) {
        if (details.permissions.granted !== null) {
          details.permissions.granted.forEach((item) => {
            if (item !== null) {
              let perm = new ProjectDetailsPermission(item)
              this._permissions.granted.push(perm)
            }
          })
        }
        if (details.permissions.requests !== null) {
          details.permissions.requests.forEach((item) => {
            if (item !== null) {
              let perm = new ProjectDetailsPermission(item)
              this._permissions.requests.push(perm)
            }
          })
        }
      }
      if (details.serviceGroups !== null) {
        details.serviceGroups.forEach((sg) => {
          let newSg = new CloudProjectServiceGroup()
          newSg.loadFromObject(sg)
          this._serviceGroups.push(newSg)
        })
      }
      if (details.details !== null) {
        this._name = details.details.name
        this._projectId = details.details.projectId
        this._statusName = details.details.statusName
        this._statusColor = details.details.statusColor
        this._loginUrl = details.details.loginUrl
      }

      this._detailsLoaded = true
    }
  }

  getProjectPermissions () {
    let out = []
    if (this._permissions.granted !== null) {
      this._permissions.granted.forEach((p) => {
        out.push(p)
      })
    }
    if (this._permissions.requests !== null) {
      this._permissions.requests.forEach((p) => {
        out.push(p)
      })
    }
    return out
  }

  /**
   * @param {CloudUser} user
   */
  async connectUserWithProject (user) {
    await this.ca.makeRequest('openStack/servisne/connectOrDisconnectUserWithProject', {
      idTerminationPoint: this._idTerminationPoint,
      idUser: user.id,
      connect: true
    })
    this.users.addUserToList(user)
  }

  /**
   * @param {CloudUser} user
   */
  async removeUserFromProject (user) {
    await this.ca.makeRequest('openStack/servisne/connectOrDisconnectUserWithProject', {
      idTerminationPoint: this._idTerminationPoint,
      idUser: user.id,
      connect: false
    })
    this.users.removeUserFromList(user)
  }

  deleteProject () {
    return this.ca.makeRequest('openStack/servisne/deleteProject', {
      idTerminationPoint: this._idTerminationPoint
    })
  }

  get idTerminationPoint () {
    return this._idTerminationPoint
  }

  set idTerminationPoint (value) {
    this._idTerminationPoint = value
  }

  get name () {
    return this._name
  }

  set name (value) {
    this._name = value
  }

  get owner () {
    return this._owner
  }

  set owner (value) {
    this._owner = value
  }

  get maintainer () {
    return this._maintainer
  }

  set maintainer (value) {
    this._maintainer = value
  }

  /**
   * @returns {CloudUserList}
   */
  get users () {
    return this._users
  }

  set users (value) {
    this._users = value
  }

  get detailsLoaded () {
    return this._detailsLoaded
  }

  set detailsLoaded (value) {
    this._detailsLoaded = value
  }

  get ca () {
    if (this._ca === null) {
      this._ca = new ClientAgent()
    }
    return this._ca
  }

  set ca (value) {
    this._ca = value
  }

  get permissions () {
    return this._permissions
  }

  set permissions (value) {
    this._permissions = value
  }

  get serviceGroups () {
    return this._serviceGroups
  }

  set serviceGroups (value) {
    this._serviceGroups = value
  }

  get projectId () {
    return this._projectId
  }

  set projectId (value) {
    this._projectId = value
  }

  get statusName () {
    return this._statusName
  }

  set statusName (value) {
    this._statusName = value
  }

  get statusColor () {
    return this._statusColor
  }

  set statusColor (value) {
    this._statusColor = value
  }

  get siteId () {
    return this._siteId
  }

  set siteId (value) {
    this._siteId = value
  }

  get siteName () {
    return this._siteName
  }

  set siteName (value) {
    this._siteName = value
  }

  get loginUrl () {
    return this._loginUrl
  }

  set loginUrl (value) {
    this._loginUrl = value
  }
}



// WEBPACK FOOTER //
// ./src/assets/js/T2Cloud/CloudProject.js